import React, { useState } from 'react';
import { HelpCircle } from 'lucide-react';
import HelpPanel from './HelpPanel';
import {
  TrustPortalScreenId,
  getTrustPortalHelpEntry,
} from '../../data/trustPortalHelpText';

// ──────────────────────────────────────────────────────────────────────────
// HelpIconButton — Trust Portal-specific (?) button that opens HelpPanel.
// ──────────────────────────────────────────────────────────────────────────
//
// Usage:
//   <HelpIconButton screenId="trust-portal-overview" />
//
// This is the Trust Portal wrapper around the generic HelpPanel. It
// handles (a) looking up the entry from trustPortalHelpText.ts and
// (b) managing the panel open/close state. For Admin → Security screens,
// there is a sibling component `AdminSecurityHelpButton` that does the
// equivalent lookup from adminSecurityHelpText.ts.
//
// Both wrappers share the same HelpPanel render logic, so adding a new
// help-enabled domain only requires a new data file + a new thin wrapper
// component — no HelpPanel changes needed.

interface HelpIconButtonProps {
  screenId: TrustPortalScreenId;
  // Optional sizing tweaks so the button can match the host screen's
  // header weight. Defaults are tuned for a ~20px icon in a standard
  // page header; use "sm" for compact contexts like a secondary card.
  size?: 'sm' | 'md';
  // Optional label shown next to the icon. Off by default so the button
  // stays visually unobtrusive — turn on if a screen really needs the
  // extra affordance.
  showLabel?: boolean;
  // Optional className passthrough for host-specific styling.
  className?: string;
}

const HelpIconButton: React.FC<HelpIconButtonProps> = ({
  screenId,
  size = 'md',
  showLabel = false,
  className = '',
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const iconSizeClass = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  const paddingClass = size === 'sm' ? 'p-1.5' : 'p-2';

  const entry = getTrustPortalHelpEntry(screenId);

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        className={`inline-flex items-center gap-1.5 ${paddingClass} rounded-full text-gray-500 hover:text-blue-600 hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-200 ${className}`}
        title="Open help for this screen"
        aria-label="Open help for this screen"
      >
        <HelpCircle className={iconSizeClass} />
        {showLabel && (
          <span className="text-sm font-medium">Help</span>
        )}
      </button>

      <HelpPanel
        entry={entry}
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
      />
    </>
  );
};

export default HelpIconButton;
